/**
 * QuotePreparationDrawer — full preparation & send workflow (left or right drawer).
 */

import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatQuoteMoney } from "../services/quoteOperationalStatus.js";
import {
  buildEnrichmentComparisonRows,
  buildReviewNextSteps,
  describeAdoptButtonState,
  describeBlocker,
  describeReviewReason,
  describeSecondaryEnrichmentWait,
  formatCatalogueMatchStatusLabel,
  formatEnrichmentM3,
  formatEnrichmentSourceLabel,
  listAdoptGuidanceSources,
  listEnrichmentReportSources,
  resolveSecondaryEnrichmentSource,
  shouldOfferRelaunchEstimation,
  summarizeEnrichmentReport,
} from "../utils/automatedPreparationReviewGuidance.js";
import QuoteReadinessChecklist from "./QuoteReadinessChecklist.jsx";
import VolumeMetricHelpBubble from "./VolumeMetricHelpBubble.jsx";
import AddCorridorRateModal from "./AddCorridorRateModal.jsx";

const HeaderMetricLabel = ({ label, metric }) => (
  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0}>
    <span>{label}</span>
    <VolumeMetricHelpBubble metric={metric} />
  </Stack>
);

const QuotePreparationDrawer = ({
  open = false,
  onClose = null,
  anchor = "left",
  quote = null,
  review = null,
  approved = false,
  loading = false,
  error = null,
  approving = false,
  adopting = false,
  adoptingPricing = false,
  repricing = false,
  waitingForRevision = false,
  onApprove = null,
  onReprice = null,
  onAdopt = null,
  onAdoptPricing = null,
  onOpenCatalogueMatch = null,
  getCatalogueCellSummary = null,
}) => {
  const [corridorModalOpen, setCorridorModalOpen] = useState(false);

  if (!quote) return null;

  const inventoryItems = review?.inventory?.items || [];
  const pricingLines = review?.pricingLines || [];
  const reasons = review?.reviewReasons || [];
  const blockers = review?.blockers || [];
  const pdfs = review?.pdfs || {};
  const nextSteps = review ? buildReviewNextSteps(review) : null;
  const enrichmentReports = review?.enrichmentReports || null;
  const catalogueReport = enrichmentReports?.catalogue || null;
  const aiReport = enrichmentReports?.ai || null;
  const n8nReport = enrichmentReports?.n8n || null;
  const secondarySource = resolveSecondaryEnrichmentSource(review);
  const secondaryReport =
    secondarySource === "n8n" ? n8nReport : secondarySource === "ai" ? aiReport : null;
  const enrichmentSlots = listEnrichmentReportSources(
    enrichmentReports,
    secondarySource
  );
  const adoptGuidanceSources = listAdoptGuidanceSources(review);
  const showRelaunchEstimation = shouldOfferRelaunchEstimation(review);
  const secondaryWaitFailed =
    review?.secondaryEnrichmentStatus?.status === "failed" && !secondaryReport;
  const catalogueSummary = summarizeEnrichmentReport(catalogueReport);
  const secondarySummary = summarizeEnrichmentReport(secondaryReport);
  const comparisonRows = buildEnrichmentComparisonRows(
    catalogueReport,
    secondaryReport,
    secondarySource
  );
  const showEnrichmentSection = Boolean(catalogueReport || secondaryReport);
  const corridor = review?.corridorGuidance || null;
  const suggestedTotal = review?.suggestedTotal ?? review?.pricingExplanation?.finalTotal;
  const committedTotal = review?.committedTotal ?? review?.totalCost;
  const route = review?.route || {};

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      data-testid="quote-preparation-drawer"
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560, md: 640 },
          maxWidth: "100vw",
        },
      }}
    >
      <Box
        sx={{
          p: 2.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box pr={1}>
            <Typography variant="overline" color="text.secondary">
              Pricing summary
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {quote.id}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Engine suggestion — Adopt price to update the quote
            </Typography>
          </Box>
          <IconButton aria-label="Close preparation drawer" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ flex: 1, overflow: "auto", pr: 0.5 }}>
          {!loading && review && (
            <>
              <Alert
                severity={review.pricingIsCommitted ? "success" : "info"}
                sx={{ mb: 1.5 }}
                data-testid="pricing-suggestion-banner"
              >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {review.pricingIsCommitted
                    ? "Quote matches last used suggested price"
                    : "Suggested price (not yet on quote)"}
                </Typography>
                <Stack direction="row" gap={2} flexWrap="wrap">
                  <Typography variant="body2">
                    Suggested:{" "}
                    <strong>
                      {suggestedTotal != null
                        ? formatQuoteMoney(suggestedTotal)
                        : "—"}
                    </strong>
                  </Typography>
                  <Typography variant="body2">
                    On quote:{" "}
                    <strong>
                      {committedTotal != null
                        ? formatQuoteMoney(committedTotal)
                        : "—"}
                    </strong>
                  </Typography>
                </Stack>
                {suggestedTotal != null && !review.pricingIsCommitted && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.75 }} color="text.secondary">
                    Suggested price is a recommendation. Use it below or enter a
                    customer price manually — it does not become the quote until you choose.
                  </Typography>
                )}
              </Alert>

              {(route.from || route.to) && (
                <Box mb={1.5} data-testid="pricing-route-summary">
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Route
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {route.from || "—"} → {route.to || "—"}
                  </Typography>
                  {route.routeKm != null && (
                    <Typography variant="caption" color="text.secondary">
                      Distance: {Number(route.routeKm).toFixed(1)} km
                    </Typography>
                  )}
                </Box>
              )}

              {corridor?.status === "missing" && (
                <Alert
                  severity="error"
                  sx={{ mb: 1.5 }}
                  data-testid="corridor-missing-alert"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={() => setCorridorModalOpen(true)}
                      data-testid="open-add-corridor-modal"
                      sx={{ whiteSpace: "nowrap", mt: 0.5 }}
                    >
                      Add corridor
                    </Button>
                  }
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Corridor rate missing
                  </Typography>
                  <Typography variant="body2">{corridor.message}</Typography>
                  {corridor.suggestedCorridor?.id && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Suggested corridor id:{" "}
                      <strong>{corridor.suggestedCorridor.id}</strong> (
                      {corridor.originRegion} → {corridor.destinationRegion},
                      shared load)
                    </Typography>
                  )}
                </Alert>
              )}

              {(corridor?.status === "exclusive_policy_pending" ||
                corridor?.status === "exclusive_rate_missing") && (
                <Alert
                  severity="warning"
                  sx={{ mb: 1.5 }}
                  data-testid={
                    corridor.status === "exclusive_policy_pending"
                      ? "exclusive-policy-pending-alert"
                      : "exclusive-rate-missing-alert"
                  }
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {corridor.status === "exclusive_policy_pending"
                      ? "Exclusive pricing pending"
                      : "Exclusive rate missing"}
                  </Typography>
                  <Typography variant="body2">{corridor.message}</Typography>
                  {corridor.corridorId && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Corridor {corridor.corridorId} — exclusive load (not Shared
                      Load)
                    </Typography>
                  )}
                </Alert>
              )}

              {corridor?.status === "live" && corridor.corridorId && (
                <Alert severity="success" sx={{ mb: 1.5 }} data-testid="corridor-live-alert">
                  <Typography variant="body2" fontWeight={600}>
                    Commercial corridor {corridor.corridorId} live
                    {corridor.ratePerM3 != null
                      ? ` · R${corridor.ratePerM3}/m³`
                      : ""}
                    {corridor.minimumAmount != null
                      ? ` · floor R${corridor.minimumAmount}`
                      : ""}
                  </Typography>
                  {(corridor.originPricingRegion ||
                    corridor.originRegion ||
                    corridor.originProvince) && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Pricing regions:{" "}
                      {corridor.originPricingRegion || corridor.originRegion} →{" "}
                      {corridor.destinationPricingRegion ||
                        corridor.destinationRegion}
                      {corridor.originProvince &&
                      corridor.destinationProvince &&
                      (corridor.originProvince !==
                        (corridor.originPricingRegion || corridor.originRegion) ||
                        corridor.destinationProvince !==
                          (corridor.destinationPricingRegion ||
                            corridor.destinationRegion))
                        ? ` · provinces ${corridor.originProvince} → ${corridor.destinationProvince}`
                        : ""}
                    </Typography>
                  )}
                  {corridor.actualRoute?.originProvince &&
                    corridor.actualRoute?.destinationProvince && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Actual route provinces:{" "}
                        {corridor.actualRoute.originProvince} →{" "}
                        {corridor.actualRoute.destinationProvince}
                      </Typography>
                    )}
                </Alert>
              )}

              <QuoteReadinessChecklist quote={quote} review={review} />
              <Divider sx={{ my: 1.5 }} />

              <Stack direction="row" gap={1} flexWrap="wrap" mb={1.5}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`State: ${review.reviewState || "unknown"}`}
                />
                {review.billableVolumeM3 != null && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Billable ${Number(review.billableVolumeM3).toFixed(2)} m³`}
                  />
                )}
                {review.inputVersion != null && (
                  <Chip size="small" variant="outlined" label={`v${review.inputVersion}`} />
                )}
              </Stack>

              {reasons.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    What needs fixing
                  </Typography>
                  {reasons.map((reason, index) => {
                    const guidance = describeReviewReason(reason.code);
                    return (
                      <Box key={`${reason.code}-${index}`} mb={index < reasons.length - 1 ? 1 : 0}>
                        <Typography variant="body2" fontWeight={600}>
                          {guidance.title}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {reason.message || guidance.detail}
                        </Typography>
                        {guidance.action && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                            → {guidance.action}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Alert>
              )}

              {nextSteps && (reasons.length > 0 || (blockers.length > 0 && !approved)) && (
                <Alert severity="info" sx={{ mb: 1.5 }} data-testid="review-next-steps">
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {nextSteps.headline}
                  </Typography>
                  {nextSteps.steps.map((step) => (
                    <Typography key={step} variant="caption" display="block">
                      • {step}
                    </Typography>
                  ))}
                </Alert>
              )}

              {blockers.length > 0 && !approved && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Approval blockers
                  </Typography>
                  {blockers.map((code) => {
                    const guidance = describeBlocker(code);
                    return (
                      <Typography key={code} variant="caption" display="block">
                        <strong>{guidance.title}</strong> — {guidance.detail}
                      </Typography>
                    );
                  })}
                </Alert>
              )}

              {showEnrichmentSection && (
                <Box mb={1.5} data-testid="dual-enrichment-reports">
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Volume enrichment reports
                  </Typography>
                  {!secondaryReport && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      {describeSecondaryEnrichmentWait(review)}
                    </Typography>
                  )}
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1} mb={1.5}>
                    {enrichmentSlots.map((source) => {
                      const report =
                        source === "catalogue"
                          ? catalogueReport
                          : source === "ai"
                            ? aiReport
                            : n8nReport;
                      const summary =
                        source === "catalogue" ? catalogueSummary : secondarySummary;
                      const isAuth = review.authoritativeEstimateSource === source;
                      return (
                        <Box
                          key={source}
                          flex={1}
                          sx={{
                            p: 1.25,
                            border: 1,
                            borderColor: isAuth ? "success.main" : "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="caption" fontWeight={700}>
                            {formatEnrichmentSourceLabel(source)}
                          </Typography>
                          {!report ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {source === "ai"
                                ? secondaryWaitFailed
                                  ? "OS AI enrichment failed"
                                  : "Waiting for OS AI enrichment…"
                                : source === "n8n"
                                  ? secondaryWaitFailed
                                    ? "n8n enrichment failed"
                                    : "Waiting for n8n…"
                                  : "No offline report"}
                            </Typography>
                          ) : (
                            <>
                              <Typography variant="body2" fontWeight={600}>
                                {formatEnrichmentM3(summary?.expectedM3)}
                              </Typography>
                              {source === "catalogue" &&
                                summary?.billableM3 != null && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    data-testid="catalogue-report-billable-m3"
                                  >
                                    Billable: {formatEnrichmentM3(summary.billableM3)}
                                  </Typography>
                                )}
                              <Typography variant="caption" color="text.secondary">
                                {summary?.confidence
                                  ? `Confidence: ${summary.confidence}`
                                  : "I don't know uniquely — best-effort estimate"}
                              </Typography>
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>

                  {catalogueReport && secondaryReport && comparisonRows.length > 0 && (
                    <Table size="small" sx={{ mb: 1.5 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell align="right">Offline m³</TableCell>
                          <TableCell align="right">
                            {formatEnrichmentSourceLabel(secondarySource)} m³
                          </TableCell>
                          <TableCell align="right">Δ</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparisonRows.map((row) => (
                          <TableRow key={row.key}>
                            <TableCell>{row.label}</TableCell>
                            <TableCell align="right">
                              {formatEnrichmentM3(row.catalogueM3)}
                            </TableCell>
                            <TableCell align="right">
                              {formatEnrichmentM3(row.secondaryM3)}
                            </TableCell>
                            <TableCell align="right">
                              {row.delta == null
                                ? "—"
                                : `${row.delta > 0 ? "+" : ""}${row.delta.toFixed(2)}`}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}

                  <Stack direction="row" gap={1} flexWrap="wrap" mb={1}>
                    {showRelaunchEstimation && (
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        disabled={
                          adopting ||
                          approving ||
                          repricing ||
                          waitingForRevision ||
                          review?.repriceStatus === "pending"
                        }
                        onClick={onReprice}
                        data-testid="relaunch-estimation"
                      >
                        {repricing ||
                        waitingForRevision ||
                        review?.repriceStatus === "pending"
                          ? "Relaunching…"
                          : "Relaunch estimation"}
                      </Button>
                    )}
                    {review.canAdoptCatalogue && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={adopting || approving || repricing}
                        onClick={() => onAdopt?.("catalogue")}
                      >
                        Adopt offline volume
                      </Button>
                    )}
                    {review.canAdoptAi && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={adopting || approving || repricing}
                        onClick={() => onAdopt?.("ai")}
                        data-testid="adopt-ai-enrichment"
                      >
                        Adopt OS AI volume
                      </Button>
                    )}
                    {review.canAdoptN8n && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={adopting || approving || repricing}
                        onClick={() => onAdopt?.("n8n")}
                      >
                        Adopt n8n volume
                      </Button>
                    )}
                  </Stack>
                  {adoptGuidanceSources.map((source) => (
                    <Typography
                      key={source}
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {describeAdoptButtonState(review, source)}
                    </Typography>
                  ))}
                </Box>
              )}

              <Stack direction="row" alignItems="center" spacing={0.5} mb={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Inventory (ops cubes vs billable m³)
                </Typography>
                <VolumeMetricHelpBubble metric="inventorySection" />
              </Stack>
              {inventoryItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  No inventory estimate items on this preparation.
                </Typography>
              ) : (
                <Table size="small" sx={{ mb: 1.5 }} data-testid="inventory-catalogue-match-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell>Catalogue</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">
                        <HeaderMetricLabel label="Ops m³" metric="ops" />
                      </TableCell>
                      <TableCell align="right">
                        <HeaderMetricLabel label="Billable m³" metric="billable" />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryItems.map((item) => {
                      const match = item.catalogueMatch;
                      const cellSummary = getCatalogueCellSummary?.(item);
                      return (
                        <TableRow key={item.key}>
                          <TableCell>{item.description || "Unnamed item"}</TableCell>
                          <TableCell>
                            <Stack spacing={0.5} alignItems="flex-start">
                              <Chip
                                size="small"
                                variant="outlined"
                                color={
                                  match?.matchStatus === "unique"
                                    ? "success"
                                    : match?.matchStatus === "ambiguous"
                                      ? "warning"
                                      : "default"
                                }
                                label={formatCatalogueMatchStatusLabel(match)}
                              />
                              {match?.matchStatus !== "unique" && match?.hint && (
                                <Typography variant="caption" color="text.secondary">
                                  {match.hint}
                                </Typography>
                              )}
                              {cellSummary && (
                                <Typography variant="caption" color="text.secondary">
                                  {cellSummary}
                                </Typography>
                              )}
                              <Button
                                size="small"
                                variant="text"
                                sx={{ px: 0, minWidth: 0, textTransform: "none" }}
                                onClick={() => onOpenCatalogueMatch?.(item)}
                              >
                                View catalogue match
                              </Button>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">
                            {item.opsM3 != null
                              ? Number(item.opsM3).toFixed(2)
                              : item.totalM3 != null
                                ? Number(item.totalM3).toFixed(2)
                                : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {item.billableM3 != null
                              ? Number(item.billableM3).toFixed(2)
                              : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}

              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Sell lines (suggested)
              </Typography>
              {pricingLines.length === 0 ? (
                <Typography variant="body2" color="text.secondary" mb={1.5}>
                  No priced sell lines available.
                </Typography>
              ) : (
                <Table size="small" sx={{ mb: 1.5 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pricingLines.map((line) => (
                      <TableRow key={line.key}>
                        <TableCell>{line.description}</TableCell>
                        <TableCell align="right">
                          {formatQuoteMoney(line.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell>
                        <strong>Customer total</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          {suggestedTotal != null
                            ? formatQuoteMoney(suggestedTotal)
                            : "—"}
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}

              <Stack direction="row" gap={1} flexWrap="wrap" mb={1.5}>
                <Chip
                  size="small"
                  color={pdfs.quotePdf?.ready ? "success" : "default"}
                  label={`Quote PDF: ${pdfs.quotePdf?.status || "missing"}`}
                />
                <Chip
                  size="small"
                  color={pdfs.quoteItemsListPdf?.ready ? "success" : "default"}
                  label={`Items PDF: ${pdfs.quoteItemsListPdf?.status || "missing"}`}
                />
              </Stack>
            </>
          )}
        </Box>

        <Box pt={2} borderTop={1} borderColor="divider">
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              color="primary"
              disabled={
                adoptingPricing ||
                adopting ||
                repricing ||
                review?.canAdoptPricing === false ||
                suggestedTotal == null
              }
              onClick={onAdoptPricing}
              data-testid="adopt-automated-pricing"
            >
              {adoptingPricing ? "Applying…" : "Use suggested price"}
            </Button>
            <Button
              variant="outlined"
              disabled={
                repricing ||
                waitingForRevision ||
                review?.canReprice === false ||
                review?.repriceStatus === "pending"
              }
              onClick={onReprice}
              data-testid="reprice-automated-preparation"
            >
              {repricing || waitingForRevision || review?.repriceStatus === "pending"
                ? "Calculating…"
                : "Calculate suggested price"}
            </Button>
            {!approved ? (
              <Button
                variant="outlined"
                disabled={
                  approving ||
                  repricing ||
                  waitingForRevision ||
                  review?.preparationReady === false
                }
                onClick={onApprove}
                data-testid="approve-ready-to-send"
              >
                {approving ? "Approving…" : "Approve for send"}
              </Button>
            ) : (
              <Typography variant="body2" color="success.main" sx={{ alignSelf: "center" }}>
                Approved for send
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>
      <AddCorridorRateModal
        open={corridorModalOpen && corridor?.status === "missing"}
        onClose={() => setCorridorModalOpen(false)}
        suggested={
          corridor?.suggestedCorridor || {
            id: corridor?.corridorId,
            originRegion: corridor?.originRegion,
            destinationRegion: corridor?.destinationRegion,
            serviceType: corridor?.serviceType || "shared_load",
          }
        }
        onSaved={async () => {
          if (typeof onReprice === "function") {
            await onReprice();
          }
        }}
      />
    </Drawer>
  );
};

export default QuotePreparationDrawer;
